import {
  DrawerBackdrop,
  DrawerContent,
  DrawerRoot,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { PopoverContent, PopoverRoot, PopoverTrigger } from '@/components/ui/popover';
import { useNotificationUnreadCountQuery } from '@/hooks/queries/use-notifications-query';
import { formatBadgeCount } from '@/lib/format-badge-count';
import { NotificationBellIcon } from '@/components/icons';
import { Box, Button, Circle, VisuallyHidden } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationsPanel } from '../../notifications/NotificationsPanel';
import { NAV_DARK_FOCUS_RING, useIsMobileLayout } from '../layout/nav-dark';

export default function NotificationBell() {
  const { t } = useTranslation();
  const isMobile = useIsMobileLayout();
  const [open, setOpen] = useState(false);
  const unreadCount = useNotificationUnreadCountQuery();
  const totalUnread = unreadCount.data?.total ?? 0;
  const closePanel = useCallback(() => setOpen(false), []);
  const handleOpenChange = useCallback((event: { open: boolean }) => {
    setOpen(event.open);
  }, []);

  const renderTrigger = useCallback(
    (onClick?: () => void) => (
      <Button
        aria-label={t('notifications.ariaLabel')}
        variant="ghost"
        rounded="full"
        h="36px"
        minW="36px"
        w="36px"
        p={0}
        color="white"
        bg="transparent"
        position="relative"
        _hover={{ bg: 'announcement.controlHoverBg' }}
        _active={{ bg: 'announcement.controlHoverBg' }}
        _open={{ bg: 'announcement.controlHoverBg' }}
        _focusVisible={NAV_DARK_FOCUS_RING}
        onClick={onClick}
      >
        <Box position="relative" display="grid" placeItems="center" w="20px" h="20px">
          <NotificationBellIcon boxSize="19px" />

          {totalUnread > 0 && (
            <Circle
              position="absolute"
              top="-7px"
              right="-8px"
              size="4"
              bg="red.500"
              color="white"
              fontSize="10px"
              fontWeight="bold"
              lineHeight="1"
              border="1.5px solid"
              borderColor="darkNav"
              pointerEvents="none"
            >
              {formatBadgeCount(totalUnread)}
            </Circle>
          )}
        </Box>
      </Button>
    ),
    [t, totalUnread]
  );

  if (isMobile) {
    return (
      <DrawerRoot open={open} onOpenChange={handleOpenChange} size="full" unmountOnExit lazyMount>
        <DrawerTrigger asChild>{renderTrigger()}</DrawerTrigger>
        <DrawerBackdrop />
        <DrawerContent>
          <VisuallyHidden>
            <DrawerTitle>{t('notifications.title')}</DrawerTitle>
          </VisuallyHidden>
          <NotificationsPanel onNavigate={closePanel} />
        </DrawerContent>
      </DrawerRoot>
    );
  }

  return (
    <PopoverRoot
      open={open}
      onOpenChange={handleOpenChange}
      positioning={{ placement: 'bottom-end', gutter: 10 }}
      lazyMount
      unmountOnExit
    >
      <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
      <PopoverContent
        w="400px"
        maxW="calc(100vw - 32px)"
        p={0}
        overflow="hidden"
        rounded="16px"
        bg="panelBg"
        border="1px solid"
        borderColor="border"
      >
        <NotificationsPanel onNavigate={closePanel} />
      </PopoverContent>
    </PopoverRoot>
  );
}
